import { LSelect } from "./ui/LSelect";
import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { loyaltyService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";

const TYPE_COLOR = { earn: "green", redeem: "red", adjust: "blue", expire: "slate", cashback: "teal" };

export default function TransactionsPage() {
  const { format } = useCurrency();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [customerId, setCustomerId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await loyaltyService.transactions({ type: type || undefined, customerId: customerId || undefined })).items || []); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [type, customerId]);
  useEffect(() => { load(); }, [load]);

  const amount = (t) => (t.balanceType === "cashback"
    ? `${t.delta >= 0 ? "+" : ""}${format(t.delta)}`
    : `${t.delta >= 0 ? "+" : ""}${t.delta} ${t.balanceType}`);

  const columns = [
    { key: "createdOn", header: "Date", render: (t) => <span className="text-sm text-muted-foreground">{new Date(t.createdOn).toLocaleString()}</span> },
    { key: "customerId", header: "Customer", render: (t) => <span className="font-mono text-sm">{t.customerId}</span> },
    { key: "type", header: "Type", render: (t) => <Badge color={TYPE_COLOR[t.type] || "slate"}>{t.type}</Badge> },
    { key: "amount", header: "Amount", render: (t) => <span className={`font-semibold ${t.delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{amount(t)}</span> },
    { key: "balanceAfter", header: "Balance After", render: (t) => <span className="text-sm">{t.balanceType === "cashback" ? format(t.balanceAfter) : (t.balanceAfter || 0).toLocaleString()}</span> },
    { key: "reason", header: "Reason", render: (t) => <span className="text-sm text-muted-foreground">{t.reason || "—"}</span> },
  ];

  return (
    <div data-testid="transactions-page">
      <PageHeader title="Transactions" subtitle="Immutable loyalty ledger — earn, redeem, adjust & expiry events" />

      <div className="mb-4 flex flex-wrap gap-3">
        <input value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="Filter by customer ID…" className="rounded-lg border border-border bg-background px-3 py-2 text-sm w-56" data-testid="txn-customer-filter" />
        <LSelect value={type} onChange={(e) => setType(e.target.value)} className="rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="txn-type-filter">
          <option value="">All types</option>
          {["earn", "redeem", "adjust", "expire", "cashback"].map((t) => <option key={t} value={t}>{t}</option>)}
        </LSelect>
      </div>

      <DataTable testid="transactions-table" rows={rows} loading={loading} columns={columns} empty="No transactions recorded yet." />
    </div>
  );
}
